import { useState } from "react";
import { instagramAdsSchema, ADS_TYPES, adsTypeLabels, type InstagramAdsPayload } from "@anton/shared";
import { PageHeader } from "../components/PageHeader";
import { Field } from "../components/Field";
import { RadioGroup } from "../components/RadioGroup";
import { SubmitButton } from "../components/SubmitButton";
import { SuccessScreen } from "../components/SuccessScreen";
import { Card, Lead, P, H3, CheckList, Stat, SectionHeader } from "../components/Section";
import { submitApplication } from "../lib/api";
import { haptic, showAlert } from "../lib/telegram";

export default function InstagramAds() {
  const [form, setForm] = useState<Partial<InstagramAdsPayload>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (done) return <SuccessScreen message="Мой ассистент Алёна свяжется, пришлёт полный прайс и подберёт оптимальный пакет под твою задачу." />;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = instagramAdsSchema.safeParse(form);
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      for (const i of parsed.error.issues) fe[i.path.join(".")] = i.message;
      setErrors(fe);
      haptic("error");
      return;
    }
    setLoading(true);
    const res = await submitApplication("INSTAGRAM_ADS", parsed.data);
    setLoading(false);
    if (!res.ok) { showAlert(res.error ?? "Ошибка"); haptic("error"); return; }
    setDone(true);
  }

  return (
    <div className="pb-10">
      <PageHeader
        eyebrow="Реклама"
        title="Реклама в моём Instagram"
        subtitle="@anton.karotki — бизнес-аудитория, предприниматели, эксперты, инвесторы."
        icon="📣"
        iconColor="red"
      />
      <div className="px-5 mt-3 space-y-3">
        <Card>
          <Lead>
            Если твоя ЦА — предприниматели и платёжеспособная аудитория, ты попал куда надо.
          </Lead>
        </Card>

        <div className="grid grid-cols-2 gap-3">
          <Stat value="90K+" label="подписчиков IG" />
          <Stat value="10K+" label="просмотров сторис" />
        </div>

        <Card>
          <H3>Форматы — от лёгкого до контрактного</H3>
          <div className="space-y-2 mt-2">
            <div className="ios-callout-muted">
              <b>OPTIMA</b><br/>3 сторис прогрева + 2 сторис рекламы.
            </div>
            <div className="ios-callout-muted">
              <b>OPTIMA PLUS</b><br/>Всё из OPTIMA + пост в Telegram-канале.
            </div>
            <div className="ios-callout-muted">
              <b>PREMIUM</b><br/>OPTIMA PLUS + 1 пост или Reels + 2 репоста в сторис.
            </div>
            <div className="ios-callout-muted">
              <b>EXCLUSIVE</b><br/>Интервью со мной как с интервьюером (живьём на Бали или онлайн). Готовое видео + анонсы + тизер.
            </div>
            <div className="ios-callout-muted">
              <b>Контракт 3 месяца</b><br/>5 сторис + пост/Reels в месяц.
            </div>
            <div className="ios-callout-muted">
              <b>Контракт 6 месяцев</b><br/>5 сторис + пост/Reels + пост в Telegram.
            </div>
            <div className="ios-callout-muted">
              <b>Контракт 12 месяцев</b><br/>Эксклюзив по нише (больше не рекламирую конкурентов) + 5 сторис + пост/Reels + пост в Telegram раз в 3 мес.
            </div>
          </div>
        </Card>

        <Card>
          <H3>Сценарий</H3>
          <P>
            Можем работать по твоему ТЗ или сценарий разрабатывает моя команда — с учётом интересов аудитории.
            Делаем так, чтобы рекламный креатив звучал органично, а не как «купленный пост».
          </P>
        </Card>

        <Card>
          <H3>Что НЕ рекламируем</H3>
          <CheckList items={[
            "Ставки на спорт",
            "Алкоголь",
          ]} />
        </Card>

        <div className="ios-callout-muted">
          Цены — по запросу. Полный прайс с актуальными условиями пришлёт мой ассистент Алёна после заявки.
        </div>
      </div>

      <SectionHeader>Оставить заявку</SectionHeader>
      <form noValidate onSubmit={onSubmit} className="px-5 space-y-4">
        <Field label="Имя" error={errors["name"]}>
          <input className={`ios-input ${errors["name"] ? "ios-input-error" : ""}`}
            value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Контакт" error={errors["contact"]}>
          <input className={`ios-input ${errors["contact"] ? "ios-input-error" : ""}`}
            value={form.contact ?? ""} onChange={(e) => setForm({ ...form, contact: e.target.value })} />
        </Field>
        <Field label="Аккаунт в Instagram" error={errors["instagram"]}>
          <input className={`ios-input ${errors["instagram"] ? "ios-input-error" : ""}`}
            value={form.instagram ?? ""} onChange={(e) => setForm({ ...form, instagram: e.target.value })}
            placeholder="@username или ссылка" />
        </Field>
        <Field label="Что хочешь рекламировать?" error={errors["whatToAdvertise"]}>
          <textarea rows={3} className={`ios-input ${errors["whatToAdvertise"] ? "ios-input-error" : ""}`}
            value={form.whatToAdvertise ?? ""} onChange={(e) => setForm({ ...form, whatToAdvertise: e.target.value })} />
        </Field>
        <Field label="Желаемый результат" error={errors["expectedResult"]}>
          <input className={`ios-input ${errors["expectedResult"] ? "ios-input-error" : ""}`}
            value={form.expectedResult ?? ""} onChange={(e) => setForm({ ...form, expectedResult: e.target.value })}
            placeholder="лиды / продажи / охваты / репутация" />
        </Field>
        <Field label="Разово или контракт?" error={errors["type"]}>
          <RadioGroup value={form.type} onChange={(v) => setForm({ ...form, type: v as any })}
            options={ADS_TYPES.map((v) => ({ value: v, label: adsTypeLabels[v] }))} />
        </Field>
        <div className="pt-2">
          <SubmitButton loading={loading}>Получить полный прайс</SubmitButton>
        </div>
      </form>
    </div>
  );
}
